import { request } from '@/utils/request';
import type { ApiResponse, WrappedResult } from './types';

// 60 s — above axios's 30 s default. During upload bursts the API node
// briefly slows down and a 30 s polling GET can hit the default timeout
// while the backend is still working on the response. 60 s leaves enough
// headroom for the worst case without making genuine outages feel any
// slower (the next poll tick is only 5 s away anyway).
const LIST_TIMEOUT_MS = 60_000;

const documentImportsApi = {
  getAll(
    params?: cyrian.api.documentImports.GetParams,
  ): ApiResponse<WrappedResult<{ items: cyrian.api.documentImports.DocumentImport[]; count: number }>> {
    return request({
      method: 'get',
      url: '/document-imports',
      timeout: LIST_TIMEOUT_MS,
      params,
    });
  },
  submitWaybillPdf(file: File): ApiResponse<WrappedResult<cyrian.api.documentImports.DocumentImport>> {
    const fd = new FormData();
    fd.append('file', file);
    return request({
      method: 'post',
      url: '/document-imports/waybill-pdf',
      headers: {
        Accept: '*/*',
        'Content-Type': 'multipart/form-data',
      },
      // Wall-clock here is dominated by the actual upload to backend; backend
      // own work is sub-second (S3 PUT + Redis enqueue).
      timeout: 600000,
      data: fd,
    });
  },
  retry<P = cyrian.api.documentImports.WaybillPdfParsed>(
    id: number,
    params?: cyrian.api.documentImports.RetryParams<P>,
  ): ApiResponse<WrappedResult<cyrian.api.documentImports.DocumentImport<P>>> {
    return request({
      method: 'post',
      url: `/document-imports/${id}/retry`,
      data: params,
    });
  },
  forceReparse(id: number): ApiResponse<WrappedResult<cyrian.api.documentImports.DocumentImport>> {
    return request({
      method: 'post',
      url: `/document-imports/${id}/force-reparse`,
    });
  },
  reapply(id: number): ApiResponse<WrappedResult<cyrian.api.documentImports.DocumentImport>> {
    return request({
      method: 'post',
      url: `/document-imports/${id}/reapply`,
    });
  },
  delete(id: number): ApiResponse<WrappedResult<{ success: boolean }>> {
    return request({
      method: 'delete',
      url: `/document-imports/${id}`,
    });
  },
};

export default documentImportsApi;
